import { apiFetch } from "@core/http";
import type {
  SchedulingException,
  SchedulingExceptionCreateInput,
  SchedulingHoursDay,
  SchedulingHoursDayInput,
  SchedulingQuotaBatchInput,
  SchedulingQuotaBatchResult,
  SchedulingQuotaCreateInput,
  SchedulingQuotaOverview,
  SchedulingQuotaSlot,
  SchedulingWorkshop,
} from "@core/types";

export type {
  SchedulingException,
  SchedulingExceptionCreateInput,
  SchedulingHoursDay,
  SchedulingHoursDayInput,
  SchedulingQuotaBatchInput,
  SchedulingQuotaBatchResult,
  SchedulingQuotaCreateInput,
  SchedulingQuotaOverview,
  SchedulingQuotaSlot,
  SchedulingWorkshop,
} from "@core/types";

// api/contracts/parts/60-portal-scheduling.yaml (SPEC-207): diez rutas bajo
// /v1/portal/scheduling. La API ya está construida, mergeada y probada — este
// módulo solo transporta, no reimplementa ninguna regla de negocio.
const base = "/v1/portal/scheduling";
const workshopBase = (workshopId: string) =>
  `${base}/workshops/${encodeURIComponent(workshopId)}`;

export const listWorkshops = () =>
  apiFetch<SchedulingWorkshop[]>(`${base}/workshops`);

export const getWorkshopHours = (workshopId: string) =>
  apiFetch<SchedulingHoursDay[]>(`${workshopBase(workshopId)}/hours`);

export const replaceWorkshopHours = (
  workshopId: string,
  hours: SchedulingHoursDayInput[],
) =>
  apiFetch<SchedulingHoursDay[]>(`${workshopBase(workshopId)}/hours`, {
    method: "PUT",
    body: hours,
  });

export const getWorkshopQuotas = (workshopId: string, day: number) =>
  apiFetch<SchedulingQuotaOverview>(
    `${workshopBase(workshopId)}/quotas?day=${String(day)}`,
  );

export const createWorkshopQuotaSlot = (
  workshopId: string,
  input: SchedulingQuotaCreateInput,
) =>
  apiFetch<SchedulingQuotaSlot>(`${workshopBase(workshopId)}/quotas`, {
    method: "POST",
    body: input,
  });

export const updateWorkshopQuotas = (
  workshopId: string,
  input: SchedulingQuotaBatchInput,
) =>
  apiFetch<SchedulingQuotaBatchResult>(`${workshopBase(workshopId)}/quotas`, {
    method: "PUT",
    body: input,
  });

export const listSchedulingExceptions = (workshopId: string) =>
  apiFetch<SchedulingException[]>(`${workshopBase(workshopId)}/exceptions`);

export const createSchedulingException = (
  workshopId: string,
  input: SchedulingExceptionCreateInput,
) =>
  apiFetch<SchedulingException>(`${workshopBase(workshopId)}/exceptions`, {
    method: "POST",
    body: input,
  });

export const deleteSchedulingException = (
  workshopId: string,
  exceptionId: string,
) =>
  apiFetch<void>(
    `${workshopBase(workshopId)}/exceptions/${encodeURIComponent(exceptionId)}`,
    { method: "DELETE" },
  );

// RF-AGE-05: la integración real con el CRM no forma parte de esta entrega
// (SPEC-207 §6, P8-1) — siempre responde 503 CRM_UNAVAILABLE. Existe para
// que la corrida de SPEC-401 solo tenga que conectar el otro extremo.
export const syncWorkshopSchedule = (workshopId: string) =>
  apiFetch<void>(`${workshopBase(workshopId)}/sync`, { method: "POST" });
